// ─────────────────────────────────────────────────────────────────────────────
// MasterSwitch — global kill-switch toggle pinned to the top of the main area.
// Ringy parity. Admin/superadmin can toggle. Everyone sees state.
// When OFF: drip messages pause and vendor leads hold as 'pending-master-off'.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useEffect, useState } from 'react';
import { Power, AlertTriangle, Loader2 } from 'lucide-react';
import { apiGet, apiPost } from '../lib/api.js';
import { useAuth } from '../store/auth.js';
import toast from 'react-hot-toast';

export default function MasterSwitch() {
  const { isAdmin, isSuperadmin } = useAuth();
  const canToggle = isAdmin?.() || isSuperadmin?.();
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    try {
      const r = await apiGet('/tenant/master-switch');
      setState(r);
    } catch (_) { /* not logged in yet */ }
  };

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 30 * 1000);
    return () => clearInterval(t);
  }, []);

  const toggle = async () => {
    if (!canToggle) {
      toast.error('Admins only');
      return;
    }
    const next = !state?.enabled;
    if (!next) {
      const ok = window.confirm(
        'Turn Master Switch OFF?\n\nThis pauses ALL drip campaigns and queues incoming vendor leads as pending. You can re-enable any time.'
      );
      if (!ok) return;
    }
    setBusy(true);
    try {
      const r = await apiPost('/tenant/master-switch/toggle', { enabled: next, reason: 'USER_TOGGLE' });
      setState((s) => ({ ...(s || {}), enabled: r.masterSwitch.enabled, disabledReason: r.masterSwitch.disabledReason }));
      toast.success(next ? 'Master Switch ON — drips resumed' : 'Master Switch OFF — outbound paused');
    } catch (e) {
      toast.error(e.response?.data?.error || 'Toggle failed');
    } finally {
      setBusy(false);
    }
  };

  if (!state) return null;

  const isOn = state.enabled;
  const reason = state.disabledReason;

  return (
    <div className={`mb-4 panel px-4 py-2 flex items-center justify-between gap-3 ${
      !isOn ? 'border-amber-500/40 bg-amber-500/5' : ''
    }`}>
      <div className="flex items-center gap-3 min-w-0">
        <div className={`relative w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
          isOn ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'
        }`}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Power className="w-4 h-4" />}
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold tracking-tight">Master Switch</span>
            <span className={`text-[10px] uppercase tracking-wide font-bold px-1.5 py-0.5 rounded ${
              isOn ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'
            }`}>
              {isOn ? 'ON' : 'OFF'}
            </span>
            {!isOn && reason === 'NEGATIVE_BALANCE' && (
              <span className="text-[10px] uppercase tracking-wide font-bold px-1.5 py-0.5 rounded-sm bg-red-500/20 text-red-400 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" /> Negative balance
              </span>
            )}
          </div>
          <div className="text-[11px] text-white/50 truncate">
            {isOn
              ? 'Drips and vendor intake active'
              : reason === 'NEGATIVE_BALANCE'
                ? 'Auto-disabled: account balance is negative. Add funds and toggle back on.'
                : 'Drip campaigns paused. Incoming vendor leads queued.'}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {canToggle && (
          <button
            type="button"
            onClick={toggle}
            disabled={busy}
            className={`relative inline-flex items-center h-6 w-11 rounded-full transition shrink-0 ${
              isOn ? 'bg-emerald-500' : 'bg-civix-border'
            } ${busy ? 'opacity-60 cursor-not-allowed' : 'hover:opacity-90 cursor-pointer'}`}
            title={isOn ? 'Click to turn OFF' : 'Click to turn ON'}
          >
            <span
              className={`inline-block w-4 h-4 rounded-full bg-white shadow transform transition ${
                isOn ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        )}
      </div>
    </div>
  );
}
